import React, { useState, useRef, useEffect } from "react";
import "./AccountResetWindow.css";
import { ModalWrapper } from "./ModalWindow";
import LoadingIcon from "./LoadingIcon";
import GameLogo from "../Resources/logo.png";
import AnimatedCheckmark from "./AnimatedCheckmark";
import { useSendCooldown } from "./useSendCooldown";

// 인증 메일 발송 정책 안내 문구 (서버: IP/이메일당 30분에 1회)
const MAIL_LIMIT_NOTICE = "※ 인증 메일은 30분에 한 번만 전송할 수 있습니다.";

type AccountResetWindowProps = {
  handleDismiss: () => void;
  onError: (err: string) => void;
  openLoginWindow: () => void;
};

export default function AccountResetWindow({ handleDismiss, onError, openLoginWindow }: AccountResetWindowProps) {
  const [loggingIn, setLoggingIn] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [accountResetCompleted, setAccountResetCompleted] = useState<boolean>(false);
  const [showCodeInput, setShowCodeInput] = useState(false); // 이메일 제출 성공 시 true가 되어 인증 코드 입력 칸을 보여줌
  const [code, setCode] = useState<string>(""); // 인증 코드 입력 필드의 값
  const [timer, setTimer] = useState<number | null>(null); // 타이머 상태 (초 단위)
  const [userId, setUserId] = useState<string>(""); // 찾은 아이디 (표시용)

  const [email, setEmail] = useState<string>(""); // 이메일 입력 필드의 값
  const [foundUserName, setFoundUserName] = useState<string | null>(null); // verifyEmail 성공 시 받아온 userName 저장

  // 타이머 인터벌 ID를 저장할 useRef 추가
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 전송 중 중복 클릭 방지 + 전송 후 재클릭 쿨다운
  const inFlightRef = useRef(false);
  const { remaining: cooldown, start: startCooldown, check: checkCooldown } = useSendCooldown();

  // 컴포넌트 언마운트 시에만 타이머 인터벌 정리
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []); // 의존성 배열을 빈 배열([])로 두어 컴포넌트 언마운트 시에만 실행

  function validateEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newEmail = event.target.value;
    setEmail(newEmail);
    setInvalidEmail(!validateEmail(newEmail) && newEmail.length > 0);
    // 입력한 이메일에 대한 재전송 쿨다운을 확인해 버튼에 반영
    checkCooldown(validateEmail(newEmail) ? newEmail : "");
  }

  // 이메일 인증번호 전송 처리
  async function handleEmailSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loggingIn || inFlightRef.current) return;

    const isValid = validateEmail(email);
    setInvalidEmail(!isValid && email.length > 0);

    if (!email || !validateEmail(email)) {
      setInvalidEmail(true);
      onError("올바른 이메일 주소를 입력해주세요.");
      return;
    }

    // 클라이언트 재전송 쿨다운 확인 (연속 전송 방지)
    const remainingCooldown = checkCooldown(email);
    if (remainingCooldown > 0) {
      onError(`인증 메일을 이미 전송했습니다. 약 ${remainingCooldown}초 후에 다시 시도할 수 있습니다.`);
      return;
    }

    inFlightRef.current = true;
    setLoggingIn(true);
    setInvalidEmail(false);

    try {
      // 1. verifyEmail API 호출
      const verifyEmailResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/verifyEmail`, {
        method: "POST",
        body: JSON.stringify({ email }),
        headers: { "Content-Type": "application/json" },
      });

      const verifyEmailData = await verifyEmailResponse.json();

      if (!verifyEmailResponse.ok) {
        onError(verifyEmailData.msg || "이메일 확인 중 오류가 발생했습니다.");
        console.error("verifyEmail API 오류:", verifyEmailResponse.status, verifyEmailData.msg); // console.error 유지
        setLoggingIn(false);
        return;
      }

      if (verifyEmailData.userInfo && verifyEmailData.userInfo.userName) {
        setFoundUserName(verifyEmailData.userInfo.userName);
      } else {
        onError("사용자 정보를 가져오는데 실패했습니다.");
        console.error("verifyEmail 응답에 사용자 정보 누락:", verifyEmailData); // console.error 유지
        setLoggingIn(false);
        return;
      }

      // 2. 인증 코드 생성
      const authCode = Math.floor(100000 + Math.random() * 900000).toString();
      const authCodeTime = 300;

      // 3. 인증 코드 저장 (setAuthCode)
      const setAuthCodeResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/setAuthCode`, {
        method: "POST",
        body: JSON.stringify({ email, authCode, authCodeTime }),
        headers: { "Content-Type": "application/json" },
      });

      const setAuthCodeData = await setAuthCodeResponse.json();

      if (!setAuthCodeResponse.ok) {
        onError(setAuthCodeData.msg || "인증 코드 저장 중 오류가 발생했습니다.");
        console.error("setAuthCode API 오류:", setAuthCodeResponse.status, setAuthCodeData.msg); // console.error 유지
        setLoggingIn(false);
        return;
      }

      // 4. 이메일 발송 (sendMail) - setAuthCode 성공 후에만 호출
      const sendMailResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sendMail`, {
        method: "POST",
        body: JSON.stringify({ email, authCode }),
        headers: { "Content-Type": "application/json" },
      });

      const sendMailData = await sendMailResponse.json();

      if (!sendMailResponse.ok) {
        if (sendMailResponse.status === 429) {
          const mins = sendMailData.retryAfter ? Math.ceil(sendMailData.retryAfter / 60) : 30;
          startCooldown(email);
          onError(`인증 메일은 30분에 한 번만 전송할 수 있습니다. 약 ${mins}분 후에 다시 시도해주세요.`);
        } else {
          onError(sendMailData.msg || "인증 이메일 발송 중 오류가 발생했습니다.");
        }
        console.error("sendMail API 오류:", sendMailResponse.status, sendMailData.msg); // console.error 유지
        setLoggingIn(false);
        return;
      }

      // 모든 과정 성공 시 쿨다운 시작 후 다음 단계로 이동
      startCooldown(email);
      setShowCodeInput(true);
      startTimer();
    } catch (error) {
      console.error("이메일 제출 및 인증 코드 발송 중 알 수 없는 오류 발생:", error); // console.error 유지
      onError("알 수 없는 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      inFlightRef.current = false;
      setLoggingIn(false);
    }
  }

  // 인증 코드 확인 함수
  async function checkAuthCodeByUserName(email: string, authCode: string): Promise<boolean> {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/getAuthCode`, {
        method: "POST",
        body: JSON.stringify({ email }),
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorData = await response.json();
        onError(errorData.msg || `인증 코드 확인 중 서버 오류 발생 (${response.status})`);
        console.error("getAuthCode API 오류:", response.status, errorData.msg); // console.error 유지
        return false;
      }

      const data = await response.json();

      const apiAuthCode = String(data.msg || "").trim();
      const inputAuthCode = String(authCode || "").trim();

      if (apiAuthCode === inputAuthCode && apiAuthCode !== "") {
        return true;
      } else {
        onError("인증번호가 일치하지 않습니다.");
        // console.warn는 개발 중 확인용이므로 제거
        return false;
      }
    } catch (error) {
      console.error("인증 코드 확인 API 호출 중 예외 발생:", error); // console.error 유지
      onError("인증 코드 확인 중 알 수 없는 오류가 발생했습니다.");
      return false;
    }
  }

  async function handleCodeSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loggingIn) return;

    if (!email) {
      onError("이메일 정보가 없습니다. 다시 시도해주세요.");
      return;
    }
    if (!code) {
      onError("인증번호를 입력하세요.");
      return;
    }

    setLoggingIn(true);

    const isCodeValid = await checkAuthCodeByUserName(email, code);

    if (isCodeValid) {
      if (foundUserName) {
        setUserId(foundUserName);
        setAccountResetCompleted(true);
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
          setTimer(null);
        }
      } else {
        console.error("인증 성공 후 사용자 아이디 정보(foundUserName)를 찾을 수 없습니다."); // console.error 유지
        onError("사용자 정보를 가져오는데 실패했습니다.");
      }
    } else {
      // checkAuthCodeByUserName 내부에서 이미 onError 호출됨
    }

    setLoggingIn(false);
  }

  // 타이머 시작 함수 - setTimer 함수형 업데이트 방식 유지
  function startTimer() {
    // console.log 제거
    if (timerIntervalRef.current) {
      // console.log 제거
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    const initialCountdown = 300;
    setTimer(initialCountdown);
    // console.log 제거

    const interval = setInterval(() => {
      // console.log 제거
      setTimer((prevTimer) => {
        // console.log 제거
        if (prevTimer === null || prevTimer <= 0) {
          // console.log 제거
          clearInterval(interval);
          timerIntervalRef.current = null;
          onError("인증 시간이 만료되었습니다. 이메일 재전송을 시도해주세요.");
          return null;
        }

        const newTimer = prevTimer - 1;
        // console.log 제거

        if (newTimer <= 0) {
          // console.log 제거
          clearInterval(interval);
          timerIntervalRef.current = null;
          onError("인증 시간이 만료되었습니다. 이메일 재전송을 시도해주세요.");
          return null;
        }

        return newTimer;
      });
    }, 1000);

    timerIntervalRef.current = interval;
    // console.log 제거
  }

  // 아이디 찾기 완료 화면
  if (accountResetCompleted) {
    return (
      <ModalWrapper handleDismiss={handleDismiss} className="AccountReset-Window">
        <section className="AccountReset-Success-Wrapper">
          <h2 className="AccountReset-Success-Title">아이디 찾기가 완료되었습니다.</h2>
          <AnimatedCheckmark />
          <p className="AccountReset-Success">
            해당 이메일의 아이디는 <span style={{ fontWeight: "bold", color: "var(--text)" }}>{userId}</span>입니다.
          </p>
          <div className="login-button" style={{ textAlign: "center", fontSize: "16px" }}>
            <br />
            <a onClick={openLoginWindow}>로그인 창으로 돌아가기</a>
          </div>
        </section>
      </ModalWrapper>
    );
  }

  // 아이디 찾기 초기 및 인증 코드 입력 화면 (main render)
  return (
    <ModalWrapper handleDismiss={handleDismiss} className="AccountReset-Window">
      <div className="AccountReset-Wrapper">
        <div className="AccountReset-Title">
          <img src={GameLogo} alt="Logo" />
        </div>
        <div className="AccountReset-Content">
          {!showCodeInput ? (
            <form onSubmit={handleEmailSubmit}>
              <input type="text" name="email" className={`Form-Input ${invalidEmail ? "error" : ""}`} placeholder="이메일" disabled={loggingIn} value={email} onChange={handleEmailChange} />
              {invalidEmail && email.length > 0 && <div className="error-text">유효한 이메일 주소를 입력하세요.</div>}
              <button type="submit" className="Form-Submit" disabled={loggingIn || !email || invalidEmail || cooldown > 0}>
                {loggingIn ? "확인 중..." : cooldown > 0 ? `${cooldown}초 후 재시도` : "인증번호 받기"}
              </button>
              <p style={{ textAlign: "center", fontSize: "12px", color: "#888", margin: "10px 0 0" }}>{MAIL_LIMIT_NOTICE}</p>
            </form>
          ) : (
            <form onSubmit={handleCodeSubmit}>
              <input type="text" name="code" className="Form-Input" placeholder="인증번호 6자리 입력" value={code} onChange={(e) => setCode(e.target.value)} disabled={loggingIn || (timer !== null && timer <= 0)} />
              <button type="submit" className="Form-Submit" disabled={loggingIn || !code || (timer !== null && timer <= 0)}>
                {loggingIn ? "확인 중..." : timer !== null && timer > 0 ? `${Math.floor(timer / 60)}:${timer % 60 < 10 ? "0" : ""}${timer % 60} 남음` : timer !== null && timer <= 0 ? "시간 만료" : "인증 코드 확인"}
              </button>
            </form>
          )}
        </div>
      </div>
      {loggingIn && <LoadingIcon dim />}
    </ModalWrapper>
  );
}
